import { useLayoutEffect, useRef, type ReactNode } from "react";

export function H3({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <h3 className={`site-h3 ${className}`}>{children}</h3>;
}

export function LetterHeading({ text, className = "", as = "h2", accentWord }: { text: string; className?: string; as?: "h1" | "h2"; accentWord?: string }) {
  const root = useRef<HTMLHeadingElement>(null);
  const Tag = as;
  useLayoutEffect(() => {
    const element = root.current;
    if (!element || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let frame = 0; let cancelled = false; let revert: (() => void) | undefined;
    frame = requestAnimationFrame(() => { frame = requestAnimationFrame(async () => {
      const [{ gsap }, { ScrollTrigger }] = await Promise.all([import("gsap"), import("gsap/ScrollTrigger")]);
      if (cancelled || !root.current) return;
      gsap.registerPlugin(ScrollTrigger);
      const ctx = gsap.context(() => gsap.fromTo(root.current!.querySelectorAll<HTMLElement>("[data-letter]"),
        { autoAlpha: 0, yPercent: 24 },
        { autoAlpha: 1, yPercent: 0, duration: .34, stagger: .007, ease: "power2.out", scrollTrigger: { trigger: root.current, start: "top 84%", once: true } }), element);
      revert = () => ctx.revert();
      ScrollTrigger.refresh();
    }); });
    return () => { cancelled = true; cancelAnimationFrame(frame); revert?.(); };
  }, [text]);
  return <Tag ref={root} className={className} aria-label={text}>{text.split(/(\s+)/).map((part, wordIndex) => part.trim() ? <span className={`letter-word${part.replace(/[,.!?]$/, "") === accentWord ? " is-accent" : ""}`} key={`${wordIndex}-${part}`}>{[...part].map((letter, index) => <span className="letter-char" data-letter key={`${wordIndex}-${index}`}>{letter}</span>)}</span> : part)}</Tag>;
}

export function HorizontalGallery({ children }: { children: ReactNode }) {
  const root = useRef<HTMLElement>(null);
  const track = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const section = root.current; const rail = track.current;
    if (!section || !rail || matchMedia("(prefers-reduced-motion: reduce)").matches || matchMedia("(max-width: 700px)").matches) return;
    let timer = 0; let frame = 0; let cancelled = false; let revert: (() => void) | undefined;
    const resize = new ResizeObserver(() => void import("gsap/ScrollTrigger").then(({ ScrollTrigger }) => { if (!cancelled) ScrollTrigger.refresh(); }));
    resize.observe(rail);
    timer = window.setTimeout(() => { frame = requestAnimationFrame(() => { frame = requestAnimationFrame(async () => {
      const [{ gsap }, { ScrollTrigger }] = await Promise.all([import("gsap"), import("gsap/ScrollTrigger")]);
      if (cancelled || !root.current || !track.current) return;
      gsap.registerPlugin(ScrollTrigger);
      const host = root.current; const content = track.current;
      const ctx = gsap.context(() => {
        const travel = () => Math.max(0, content.scrollWidth - host.clientWidth);
        gsap.to(content, { x: () => -travel(), ease: "none", scrollTrigger: { id: "edcc-horizontal-gallery", trigger: host, start: "top top", end: () => `+=${travel()}`, pin: true, scrub: .65, invalidateOnRefresh: true, anticipatePin: 1 } });
      }, host);
      revert = () => ctx.revert();
      ScrollTrigger.refresh();
    }); }); }, 40);
    return () => { cancelled = true; clearTimeout(timer); cancelAnimationFrame(frame); resize.disconnect(); revert?.(); };
  }, []);
  return <section className="scroll-gallery" ref={root}><div className="scroll-gallery-heading"><p>FIELD NOTES / LIVE APP</p><h2>Inside the cockpit.</h2><span>Keep scrolling to browse the screens ↓</span></div><div className="scroll-gallery-window"><div className="scroll-gallery-track" ref={track}>{children}</div></div></section>;
}

export function ExpandingImage({ src, alt }: { src: string; alt: string }) {
  const section = useRef<HTMLElement>(null); const frame = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const host = section.current; const image = frame.current;
    if (!host || !image || matchMedia("(prefers-reduced-motion: reduce)").matches || matchMedia("(max-width: 700px)").matches) return;
    let timer = 0; let raf = 0; let cancelled = false; let revert: (() => void) | undefined;
    const resize = new ResizeObserver(() => void import("gsap/ScrollTrigger").then(({ ScrollTrigger }) => { if (!cancelled) ScrollTrigger.refresh(); })); resize.observe(image);
    timer = window.setTimeout(() => { raf = requestAnimationFrame(() => { raf = requestAnimationFrame(async () => {
      const [{ gsap }, { ScrollTrigger }] = await Promise.all([import("gsap"), import("gsap/ScrollTrigger")]);
      if (cancelled) return;
      gsap.registerPlugin(ScrollTrigger);
      const ctx = gsap.context(() => gsap.fromTo(image, { scale: .76, borderRadius: "3px" }, { scale: () => Math.max(window.innerWidth / image.offsetWidth, window.innerHeight / image.offsetHeight) * 1.02, borderRadius: "0px", ease: "none", scrollTrigger: { trigger: host, start: "top top", end: "bottom bottom", scrub: .7, pin: true, anticipatePin: 1, invalidateOnRefresh: true } }), host);
      revert = () => ctx.revert(); ScrollTrigger.refresh();
    }); }); }, 40);
    return () => { cancelled = true; clearTimeout(timer); cancelAnimationFrame(raf); resize.disconnect(); revert?.(); };
  }, []);
  return <section className="expand-image-section" ref={section} aria-label="EDCC app preview"><div className="expand-image-frame" ref={frame}><img src={src} alt={alt} loading="lazy" /></div></section>;
}
